import { difference } from '@turf/difference';
import { feature, featureCollection } from '@turf/turf';
import L from 'leaflet';
import 'leaflet-rotatedmarker';
import { type ReactElement, useState } from 'react';
import { MapSymbolsReference } from '../../MapSymbolsReference';
import { useBootstrapModalBlur } from '../../Util/useBootstrapModalBlur';
import { SidebarLayerControl } from '../Controls/SidebarLayerControl';
import { BIKE_SECTION, PARKING_SECTION, ROAD_CONDITION_SECTION, ROAD_SECTION } from '../Controls/SidebarLayerControl.types';
import {
    allCamerasFilter,
    bikeFilterCurrent,
    bikeFilterPlanned,
    bikeParkingGbg,
    bikeRoadsGbg,
    bikeRoadsSth,
    bikeSituation,
    getTravelTimeLayerId,
    maskArea,
    migrateTravelTimeUserLayer,
    parkingsGbgBus,
    parkingsGbgChargePrivate,
    parkingsGbgChargePublic,
    parkingsGbgDisabled,
    parkingsGbgMC,
    parkingsGbgTime,
    parkingsGbgTruck,
    pumpStation,
    removeAllFilter,
    rentalBike,
    roadCondition,
    roadConditionMessage,
    roadFilterMain,
    roadFilterPlannedMain,
    roadFilterPlannedRoadwork,
    roadFilterRoadwork,
    roadSituation,
    shuttleParkingGbg,
    trafficCamera,
    travelTime,
    travelTimeV2,
    weatherMeasurepoint,
} from '../LayerFactories';
import { allShuttleParkingsGbgFilter } from '../LayerFactories/AllShuttleParkingsGbg';
import { parkingsSthlmBus, parkingsSthlmDisabled, parkingsSthlmMC, parkingsSthlmTruck } from '../LayerFactories/ParkingsSthlm';
import { stripedPolygon } from '../LayerFactories/StripedPolygon';
import { LeafletMap } from '../Map';
import type { FilterSetupObject, LayerSetupDefinition, LayerSetupObject } from '../Map.types';
import { RoadConditionSectionSource } from '../Sources/RoadConditionSection';
import { RoadConditionStatusSource } from '../Sources/RoadConditionStatus';
import type { StartPageMapProps } from './types';

export const StartPageMap = (props: StartPageMapProps): ReactElement => {
    const mapProps = props.mapSettings;
    const region = mapProps.region;

    const [mapState, setMapState] = useState<{ map: L.Map; layers: { [key: string]: LayerSetupDefinition } }>();

    useBootstrapModalBlur('roadSymbolsModal');

    // The v2 (TomTom) and legacy travel-time layers are mutually exclusive; the
    // sidebar only renders the toggle for the flagged one, so include/default it
    // consistently to avoid loading a layer with no control to turn it off.
    const travelTimeLayerId = getTravelTimeLayerId();

    const includedLayers =
        region === 'gbg'
            ? [
                  'mask-area',
                  'extended-region-area',
                  travelTimeLayerId,
                  'road-situation',
                  'road-condition-combined',
                  'road-situations-combined',
                  'clear-situations',
                  'show-high-prio-situations',
                  'show-roadwork-situations',
                  'weather-station',
                  'show-planned-high-prio-situations',
                  'show-planned-roadwork-situations',
                  'bike-situations',
                  'clear-bike-situations',
                  'show-current-bike-situations',
                  'show-planned-bike-situations',
                  'traffic-camera',
                  'all-traffic-cameras',
                  'parkings-gbg-charge',
                  'parkings-gbg-time',
                  'parkings-gbg-bus-truck',
                  'parkings-gbg-mc',
                  'shuttle-parking-gbg',
                  'all-shuttle-parking-gbg',
                  'parkings-gbg-disabled',
                  'rental-bike',
                  'pump-station',
                  'bike-roads-gbg',
                  'bike-parkings-gbg',
              ]
            : [
                  'mask-area',
                  'extended-region-area',
                  travelTimeLayerId,
                  'road-situation',
                  'road-condition-combined',
                  'road-situations-combined',
                  'clear-situations',
                  'show-high-prio-situations',
                  'show-roadwork-situations',
                  'show-planned-high-prio-situations',
                  'show-planned-roadwork-situations',
                  'toll-booth',
                  'traffic-camera',
                  'all-traffic-cameras',
                  'weather-station',
                  'parkings-sthlm-bus-truck',
                  'parkings-sthlm-mc',
                  'parkings-sthlm-disabled',
                  'bike-roads-vst',
                  'bike-situations',
                  'clear-bike-situations',
                  'show-current-bike-situations',
                  'show-planned-bike-situations',
              ];
    const defaultLayers =
        region === 'gbg'
            ? [
                  'mask-area',
                  'extended-region-area',
                  travelTimeLayerId,
                  'road-situation',
                  'road-condition-combined',
                  'road-situations-combined',
                  'clear-situations',
                  'show-high-prio-situations',
                  'show-roadwork-situations',
                  'bike-situations',
                  'clear-bike-situations',
                  'show-current-bike-situations',
                  'show-planned-bike-situations',
                  'traffic-camera',
                  'weather-station',
                  'shuttle-parking-gbg',
                  'all-shuttle-parking-gbg',
              ]
            : [
                  'mask-area',
                  'extended-region-area',
                  travelTimeLayerId,
                  'road-situation',
                  'road-condition-combined',
                  'road-situations-combined',
                  'clear-situations',
                  'show-high-prio-situations',
                  'bike-situations',
                  'clear-bike-situations',
                  'show-current-bike-situations',
                  'show-planned-bike-situations',
                  'traffic-camera',
                  'weather-station',
                  'götaälvbron',
                  'shuttle-parking-gbg',
              ];

    const setupMap = (map: L.Map): void => {
        // Align any persisted travel-time toggle with the id the flag now uses.
        migrateTravelTimeUserLayer(region);

        const roadSituations = roadSituation(region);

        const roadConditionSectionSource = new RoadConditionSectionSource(region);
        const roadConditionStatusSource = new RoadConditionStatusSource(region, { updateInterval: 60 });
        const roadConditionLayer = roadCondition(region, {
            source: roadConditionSectionSource,
            statusSource: roadConditionStatusSource,
        });
        const roadConditionMarkerLayer = roadConditionMessage(region);
        const roadFilterMainLayer = roadFilterMain([roadSituations]);

        const layerSetup: LayerSetupObject = {
            'mask-area': { factory: maskArea, parameters: [[mapProps.regionExtent, mapProps.extendedRegionExtent].filter((area) => area)] },
            'extended-region-area': {
                factory: stripedPolygon,
                parameters: mapProps.extendedRegionExtent && [
                    difference(featureCollection([feature(mapProps.extendedRegionExtent), feature(mapProps.regionExtent)])).geometry,
                ],
            },
            'travel-time': { factory: travelTime, sections: [ROAD_SECTION, PARKING_SECTION], parameters: [region] },
            'travel-time-v2': { factory: travelTimeV2, sections: [ROAD_SECTION, PARKING_SECTION], parameters: [region] },
            'road-condition-combined': {
                factory: L.layerGroup,
                sections: [ROAD_CONDITION_SECTION],
                parameters: [[roadConditionLayer, roadConditionMarkerLayer]],
            },
            'road-situation': { factory: () => roadSituations, sections: [ROAD_SECTION] },
            'bike-situations': { factory: bikeSituation, sections: [BIKE_SECTION], parameters: [region] },
            'traffic-camera': { factory: trafficCamera, sections: [ROAD_SECTION, ROAD_CONDITION_SECTION, PARKING_SECTION], parameters: [region] },
            'weather-station': { factory: weatherMeasurepoint, sections: [ROAD_CONDITION_SECTION], parameters: [mapProps.regionExtent] },
            'parkings-gbg-charge': {
                factory: L.layerGroup,
                sections: [PARKING_SECTION],
                parameters: [[parkingsGbgChargePublic(), parkingsGbgChargePrivate()]],
                icons: ['210'],
            },
            'parkings-gbg-time': { factory: parkingsGbgTime, sections: [PARKING_SECTION], icons: ['210'] },
            'parkings-gbg-bus-truck': {
                factory: L.layerGroup,
                sections: [PARKING_SECTION],
                parameters: [[parkingsGbgBus(), parkingsGbgTruck()]],
                icons: ['204', '202'],
            },
            'shuttle-parking-gbg': { factory: shuttleParkingGbg, sections: [ROAD_SECTION, ROAD_CONDITION_SECTION, PARKING_SECTION], icons: ['208'] },
            'parkings-gbg-disabled': { factory: parkingsGbgDisabled, sections: [PARKING_SECTION], icons: ['201'] },
            'parkings-gbg-mc': { factory: parkingsGbgMC, sections: [PARKING_SECTION], icons: ['203'] },
            'parkings-sthlm-bus-truck': {
                factory: L.layerGroup,
                sections: [PARKING_SECTION],
                parameters: [[parkingsSthlmBus(), parkingsSthlmTruck()]],
                icons: ['204', '202'],
            },
            'parkings-sthlm-disabled': { factory: parkingsSthlmDisabled, sections: [PARKING_SECTION], icons: ['201'] },
            'parkings-sthlm-mc': { factory: parkingsSthlmMC, sections: [PARKING_SECTION], icons: ['203'] },
            'rental-bike': { factory: rentalBike, sections: [BIKE_SECTION] },
            'pump-station': { factory: pumpStation, sections: [BIKE_SECTION] },
            'bike-roads-gbg': { factory: bikeRoadsGbg, sections: [BIKE_SECTION], parameters: [mapProps.mapServerUrl, mapProps.mapTilesVersion] },
            'bike-roads-vst': { factory: bikeRoadsSth, sections: [BIKE_SECTION], parameters: [mapProps.mapServerUrl, mapProps.mapTilesVersion] },
            'bike-parkings-gbg': { factory: bikeParkingGbg, sections: [BIKE_SECTION] },
        };

        const filterSetup: FilterSetupObject = {
            'clear-situations': { factory: removeAllFilter, sections: [ROAD_SECTION], layers: [roadSituations] },
            'clear-bike-situations': { factory: removeAllFilter, sections: [BIKE_SECTION], layerIds: ['bike-situations'] },
            'show-roadwork-situations': { factory: roadFilterRoadwork, sections: [ROAD_SECTION], icons: ['120'], layers: [roadSituations] },
            'show-planned-high-prio-situations': {
                factory: roadFilterPlannedMain,
                sections: [ROAD_SECTION],
                icons: ['161', '164'],
                layers: [roadSituations],
            },
            'show-planned-roadwork-situations': {
                factory: roadFilterPlannedRoadwork,
                sections: [ROAD_SECTION],
                icons: ['162'],
                layers: [roadSituations],
            },
            'all-traffic-cameras': { factory: allCamerasFilter, sections: [ROAD_SECTION], icons: ['icon-camera'], layerIds: ['traffic-camera'] },
            'all-shuttle-parking-gbg': {
                factory: allShuttleParkingsGbgFilter,
                sections: [PARKING_SECTION],
                icons: ['208'],
                layerIds: ['shuttle-parking-gbg'],
            },
            'show-current-bike-situations': { factory: bikeFilterCurrent, sections: [BIKE_SECTION], layerIds: ['bike-situations'] },
            'show-planned-bike-situations': { factory: bikeFilterPlanned, sections: [BIKE_SECTION], layerIds: ['bike-situations'] },
        };

        // Initiate layers
        const baseLayers = Object.keys(layerSetup)
            .filter((key) => includedLayers.includes(key))
            .reduce((res: { [key: string]: LayerSetupDefinition }, key) => {
                res[key] = {
                    layer: layerSetup[key].factory(...(layerSetup[key].parameters || [])),
                    sections: layerSetup[key].sections,
                    icons: layerSetup[key].icons,
                    disabled: layerSetup[key].disabled,
                };
                return res;
            }, {});

        // Initiate filters
        const filterLayers = Object.keys(filterSetup)
            .filter((key) => includedLayers.includes(key))
            .reduce((res: { [key: string]: LayerSetupDefinition }, key) => {
                const filter = filterSetup[key];
                let layers =
                    filter.layerIds?.filter((layerId) => Object.keys(baseLayers).includes(layerId)).map((layerId) => baseLayers[layerId].layer) ?? [];
                layers = [...layers, ...(filter.layers ?? [])];
                res[key] = {
                    layer: filter.factory(layers, ...(filter.parameters || [])),
                    sections: filter.sections,
                    icons: filter.icons,
                };
                return res;
            }, {});

        const layers = { ...baseLayers, ...filterLayers };

        layers['road-situations-combined'] = {
            layer: L.layerGroup([roadFilterMainLayer, roadConditionMarkerLayer]),
            sections: [ROAD_SECTION],
            icons: ['101', '105', '120'],
        };

        setMapState({ map, layers });
    };

    return (
        <>
            {mapState && (
                <SidebarLayerControl
                    map={mapState.map}
                    scope={mapProps.region}
                    region={mapProps.region}
                    layers={mapState.layers}
                    defaultSection={ROAD_SECTION}
                    defaultOnLayers={defaultLayers}
                    sectionsToShow={[ROAD_SECTION, BIKE_SECTION, ROAD_CONDITION_SECTION, PARKING_SECTION]}
                    roadHelpElementId="roadSymbolsModal"
                    bikePlannerUrl={props.bikePlannerUrl}
                />
            )}
            <LeafletMap {...mapProps} savePositionInHash={true} showMyPosition={true} showMobileFullscreenOverlay={true} onMapInitiated={setupMap} />
            <div className="modal fade" id="roadSymbolsModal" tabIndex={-1} role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title" id="exampleModalLabel">
                                Teckenförklaring
                            </h2>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Stäng"></button>
                        </div>
                        <div className="modal-body">
                            <MapSymbolsReference region={mapProps.region} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
